import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PatientList.css';
import { useNav } from '../../components/NavContext';

const AdminOnlineNotes = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const DOMAIN = process.env.REACT_APP_DOMAIN;
  const { addLink } = useNav();

  // Populate admin navigation links (same menu items as other admin pages)
  useEffect(() => {
    addLink('اطبائنا', `${DOMAIN}control/doctor-list`);
    addLink('اضافة طبيب', `${DOMAIN}control/doctor-create`);
    addLink('online', `${DOMAIN}control/admin/online-notes`);
  }, [addLink, DOMAIN]);

  // Filters
  const [doctorId, setDoctorId] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [perPage, setPerPage] = useState(20);

  // Data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 20, total: 0 });

  // Debounce filters to avoid overfetching on typing
  const filterState = { doctorId, status, type, dateFrom, dateTo, perPage };
  const [debouncedFilters, setDebouncedFilters] = useState(filterState);
  const debounceTimer = useRef(null);
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedFilters({ doctorId, status, type, dateFrom, dateTo, perPage }), 300);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
  }, [doctorId, status, type, dateFrom, dateTo, perPage]);

  const fetchNotes = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

            
      const params = new URLSearchParams();
      params.append('per_page', String(debouncedFilters.perPage || 20));
      params.append('page', String(page));
      if (debouncedFilters.doctorId) params.append('doctor_id', debouncedFilters.doctorId);
      if (debouncedFilters.status) params.append('status', debouncedFilters.status);
      if (debouncedFilters.type) params.append('type', debouncedFilters.type);
      if (debouncedFilters.dateFrom) params.append('date_from', debouncedFilters.dateFrom);
      if (debouncedFilters.dateTo) params.append('date_to', debouncedFilters.dateTo);

      const response = await axios.get(`${API_URL}admin/notes/online?${params.toString()}`, {
        headers: { 'Accept': 'application/json' },
      });

      const data = response.data;
      if (!data || !Array.isArray(data.data)) {
        throw new Error('Invalid response from server');
      }

      const normalized = data.data.map((n) => ({
        id: n.id,
        date: n.date,
        type: n.type,
        status: n.status,
        appointment_note: n.appointment_note,
        description: n.description,
        patient: n.patient || null,
        doctor_name: n.doctor_name || '',
        created_at: n.created_at || null,
      }));

      setNotes(normalized);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      });
    } catch (err) {
      console.error('Fetch admin online notes error:', err);
      setError(err.message || 'Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  const gotoPage = (page) => {
    if (page < 1 || page > pagination.last_page) return;
    fetchNotes(page);
  };

  const renderDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/);
      if (m) {
        const year = parseInt(m[1], 10);
        const month = parseInt(m[2], 10);
        const day = parseInt(m[3], 10);
        const hasTime = !!m[4];
        const hourNum = parseInt(m[4] || '0', 10);
        const minute = m[5] ? m[5].padStart(2, '0') : '00';
        const hour12 = ((hourNum % 12) || 12);
        const ampm = hourNum >= 12 ? 'PM' : 'AM';
        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const monthLabel = monthNames[Math.max(0, Math.min(11, month - 1))];
        if (hasTime) return `${monthLabel} ${day}, ${year}, ${String(hour12).padStart(2,'0')}:${minute} ${ampm}`;
        return `${monthLabel} ${day}, ${year}`;
      }
      return dateStr;
    } catch (_) {
      return dateStr;
    }
  };

  return (
    <div className="appointments-container">
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-12">
            <div className="card patient-list-card">
              <div className="card-header patient-list-header d-flex justify-content-between align-items-center">
                <h4>Online Booked Notes</h4>
                <div>
                  <button type="button" className="btn create-patient-btn" onClick={() => navigate(-1)}>
                    <i className="fas fa-arrow-left me-2"></i>Back
                  </button>
                </div>
              </div>
              <div className="card-body patient-list-body">

                {/* Filters */}
                <div className="search-filters-section">
                  <div className="row g-2 align-items-end">
                    <div className="col-md-2">
                      <label className="form-label small text-muted">Doctor ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={doctorId}
                        onChange={(e) => setDoctorId(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="e.g. 14"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small text-muted">Status</label>
                      <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">Any</option>
                        <option value="pending">Pending</option>
                        <option value="checked-in">Checked-in</option>
                        <option value="canceled">Canceled</option>
                        <option value="complete">Complete</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small text-muted">Type</label>
                      <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
                        <option value="">Any</option>
                        <option value="appointment">Appointment</option>
                        <option value="treatment">Treatment</option>
                        <option value="generic">Generic</option>
                        <option value="investigation">Investigation</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small text-muted">From</label>
                      <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small text-muted">To</label>
                      <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small text-muted">Per Page</label>
                      <select className="form-select" value={perPage} onChange={(e) => setPerPage(parseInt(e.target.value, 10) || 20)}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Results */}
                {loading ? (
                  <div className="appointments-loading">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading notes...</p>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">Error: {error}</div>
                ) : notes.length === 0 ? (
                  <div className="no-appointments-state">
                    <div className="d-flex flex-column align-items-center">
                      <i className="fas fa-file-medical-alt no-appointments-icon"></i>
                      <h5 className="no-appointments-title">No notes found</h5>
                      <p className="no-appointments-text">Try adjusting filters or date range.</p>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive patients-table-container">
                    <table className="table table-hover patients-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Patient</th>
                          <th>Doctor</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notes.map((n, index) => (
                          <tr key={n.id}>
                            <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                            <td>
                              {n.patient ? (
                                <Link to={`/control/patient/${n.patient.id}`} className="patient-name-link">
                                  {n.patient.name || 'Unknown Patient'}
                                </Link>
                              ) : (
                                <span className="text-muted">Unknown Patient</span>
                              )}
                            </td>
                            <td>{n.doctor_name || <span className="text-muted">Unknown</span>}</td>
                            <td>
                              <span className={`status-badge ${n.status === 'complete' ? 'status-seen' : 'status-pending'}`}>
                                {n.status || 'pending'}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge status-pending`}>
                                <i className="fas fa-clock me-1"></i>
                                {renderDate(n.date)}
                              </span>
                            </td>
                            <td>
                              <span className="text-muted small">
                                {n.created_at ? renderDate(n.created_at) : '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {notes.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="text-muted small">
                      Showing {(pagination.current_page - 1) * pagination.per_page + 1}
                      {' - '}
                      {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
                    </div>
                    <div className="btn-group" role="group">
                      <button className="btn btn-outline-secondary" onClick={() => gotoPage(1)} disabled={pagination.current_page <= 1}>
                        « First
                      </button>
                      <button className="btn btn-outline-secondary" onClick={() => gotoPage(pagination.current_page - 1)} disabled={pagination.current_page <= 1}>
                        ‹ Prev
                      </button>
                      <button className="btn btn-outline-secondary" onClick={() => gotoPage(pagination.current_page + 1)} disabled={pagination.current_page >= pagination.last_page}>
                        Next ›
                      </button>
                      <button className="btn btn-outline-secondary" onClick={() => gotoPage(pagination.last_page)} disabled={pagination.current_page >= pagination.last_page}>
                        Last »
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOnlineNotes;
